import { useState, useMemo, lazy, Suspense, useEffect } from "react";
import { GalleryGrid, GalleryHeader, GalleryImage, GalleryWrapper, SubHeader, } from "./styled";
import { useTranslation } from "react-i18next";
import { StaggeredContainer, StaggeredItem } from "../animations/staggeredFadeIn";
import "yet-another-react-lightbox/styles.css";
import { LINKS } from "../../config/links";

const Lightbox = lazy(() => import("yet-another-react-lightbox"));

const Gallery = ({ images, variant }) => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(-1);
    const [zoomPlugin, setZoomPlugin] = useState(null);

    useEffect(() => {
        if (index < 0) return;

        let cancelled = false;
        (async () => {
            const mod = await import("yet-another-react-lightbox/plugins/zoom");
            if (!cancelled) setZoomPlugin(() => mod.default);
        })();

        return () => {
            cancelled = true;
        };
    }, [index]);

    const galleryTitle = t(`gallery.titles.${variant}`);

    const groups = useMemo(() => {
        if (variant === "houses") {
            return {
                mode: "houses",
                main: images.slice(0, 4),
                first: images.slice(4, 9),
                second: images.slice(9),
            };
        }

        return {
            mode: "default",
            all: images,
        };
    }, [images, variant]);

    const slides = useMemo(() => images.map((img) => ({ src: img.full })), [images]);

    const renderGrid = (imgs, offset = 0) => (
        <GalleryGrid>
            {imgs.map((img, i) => {
                const absoluteIndex = offset + i;

                return (
                    <GalleryImage
                        key={`${variant}-${img.altKey}-${absoluteIndex}`}
                        src={img.thumb}
                        width={1600}
                        height={900}
                        loading={absoluteIndex < 4 ? "eager" : "lazy"}
                        decoding="async"
                        alt={t(`gallery.galleryAlt.${variant}.${img.altKey}`, {
                            defaultValue: t(`gallery.titles.${variant}`),
                        })}
                        onClick={() => setIndex(absoluteIndex)}
                    />
                );
            })}
        </GalleryGrid>
    );

    return (
        <GalleryWrapper id={LINKS.sections.gallery}>
            <StaggeredContainer>
                <StaggeredItem>
                    <GalleryHeader>{galleryTitle}</GalleryHeader>
                </StaggeredItem>

                <StaggeredItem>
                    {groups.mode === "houses" ? (
                        <>
                            {renderGrid(groups.main, 0)}

                            {groups.first.length > 0 && (
                                <>
                                    <SubHeader>{t("gallery.firstHouse")}</SubHeader>
                                    {renderGrid(groups.first, 4)}
                                </>
                            )}

                            {groups.second.length > 0 && (
                                <>
                                    <SubHeader>{t("gallery.secondHouse")}</SubHeader>
                                    {renderGrid(groups.second, 9)}
                                </>
                            )}
                        </>
                    ) : (
                        renderGrid(groups.all, 0)
                    )}
                </StaggeredItem>
            </StaggeredContainer>

            {index >= 0 && (
                <Suspense fallback={null}>
                    <Lightbox
                        open
                        close={() => setIndex(-1)}
                        index={index}
                        slides={slides}
                        plugins={zoomPlugin ? [zoomPlugin] : []}
                        zoom={{ maxZoomPixelRatio: 1.5, zoomInMultiplier: 1 }}
                        onBackdropClick={() => setIndex(-1)}
                    />
                </Suspense>
            )}
        </GalleryWrapper>
    );
};

export default Gallery;